import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { generateMarketplaceCopywriting } from '../../../../services/marketplaceCopywritingService';

import Input from '../../../../components/ui/Input';

const DescriptionTab = ({ productData, onFieldChange }) => {
  const [charCount, setCharCount] = useState(productData?.description?.length || 0);
  const [generatingDescription, setGeneratingDescription] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit],
    content: productData?.description || '',
    onUpdate: ({ editor }) => {
      const html = editor?.getHTML();
      onFieldChange('description', html);
      setCharCount(editor?.getText()?.length);
    }
  });

  const toolbarButtons = [
    { icon: 'Bold', action: () => editor?.chain()?.focus()?.toggleBold()?.run(), isActive: editor?.isActive('bold') },
    { icon: 'Italic', action: () => editor?.chain()?.focus()?.toggleItalic()?.run(), isActive: editor?.isActive('italic') },
    { icon: 'List', action: () => editor?.chain()?.focus()?.toggleBulletList()?.run(), isActive: editor?.isActive('bulletList') },
    { icon: 'ListOrdered', action: () => editor?.chain()?.focus()?.toggleOrderedList()?.run(), isActive: editor?.isActive('orderedList') }
  ];

  const handleGenerateDescription = async () => {
    try {
      setGeneratingDescription(true);
      
      // Use advanced marketplace copywriting service
      const response = await generateMarketplaceCopywriting(productData);
      
      if (response?.success) {
        const content = response?.content;

        // Update product name if generated
        if (content?.productName) {
          onFieldChange('name', content?.productName);
        }

        // Update description
        if (content?.description) {
          onFieldChange('description', content?.description);
          editor?.commands?.setContent(content?.description);
        }

        // Update differentials as tags (comma-separated)
        if (content?.differentials && Array.isArray(content?.differentials)) {
          const tagsString = content?.differentials?.join(', ');
          onFieldChange('tags', tagsString);
        }

        // Store technical specs in metadata
        if (content?.technicalSpecs) {
          const currentMetadata = productData?.metadata || {};
          onFieldChange('metadata', {
            ...currentMetadata,
            technicalSpecs: content?.technicalSpecs
          });
        }

        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'success',
            message: 'Conteúdo profissional gerado com otimização para marketplaces (Amazon, Mercado Livre, Magalu, Shopee)',
            progress: 100
          }
        }));
      } else {
        throw new Error(response?.error || 'IA não disponível');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: error?.message || 'Erro ao gerar descrição',
          progress: 100
        }
      }));
    } finally {
      setGeneratingDescription(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Rich Text Editor */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-foreground">
            Descrição do Produto
          </label>
          <Button
            onClick={handleGenerateDescription}
            disabled={generatingDescription}
            variant="outline"
            size="sm"
          >
            {generatingDescription ? (
              <>
                <Icon name="Loader" size={14} className="mr-1 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Icon name="Sparkles" size={14} className="mr-1" />
                Gerar com IA
              </>
            )}
          </Button>
        </div>
        
        {/* Toolbar */}
        <div className="flex items-center gap-1 p-2 border border-border rounded-t-lg bg-muted/50">
          {toolbarButtons?.map((btn, index) => (
            <button
              key={index}
              onClick={btn?.action}
              className={`
                p-2 rounded hover:bg-background transition-smooth
                ${btn?.isActive ? 'bg-primary text-white' : 'text-foreground'}
              `}
            >
              <Icon name={btn?.icon} size={16} />
            </button>
          ))}
        </div>
        
        {/* Editor */}
        <div className="border border-t-0 border-border rounded-b-lg p-4 min-h-[200px] prose prose-sm max-w-none">
          <EditorContent editor={editor} />
        </div>
        
        <p className="text-xs text-muted-foreground mt-1">
          {charCount} caracteres
        </p>
      </div>

      {/* Tags */}
      <Input
        label="Tags / Palavras-chave"
        value={productData?.tags}
        onChange={(e) => onFieldChange('tags', e?.target?.value)}
        placeholder="Separe por vírgula: mesa, educativa, brinquedo"
        description="Ajuda na busca e SEO"
      />
    </div>
  );
};

export default DescriptionTab;